/* eslint-disable react-hooks/rules-of-hooks */
import { useTranslation } from 'react-i18next';
import { formatter, type Task } from '@repo/shared';
import { useRouter } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import {
  ChevronDown,
  ChevronRight,
  EllipsisVertical,
  TestTube,
} from 'lucide-react';

import type { DatasetsQueryResult } from '@/api/generated';
import { ColumnHeader } from '@/components/blocks/data-table/column-header';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import TaskBadge from '@/routes/(app)/(datasets)/components/task-badge';

export const columns: Array<ColumnDef<DatasetsQueryResult['data'][number]>> = [
  {
    accessorKey: 'name',
    header: ({ column }) => {
      const { t } = useTranslation();
      return (
        <ColumnHeader
          column={column}
          title={t('datasets.table.name')}
          enableSorting
        />
      );
    },
  },
  {
    accessorKey: 'task',
    header: ({ column }) => {
      const { t } = useTranslation();
      return (
        <ColumnHeader
          column={column}
          title={t('datasets.table.task')}
          enableSorting
        />
      );
    },
    cell: ({ row }) => <TaskBadge task={row.original.task as Task} />,
  },
  {
    accessorKey: 'instances',
    header: ({ column }) => {
      const { t } = useTranslation();
      return (
        <ColumnHeader
          column={column}
          title={t('datasets.table.nrOfSamples')}
          enableSorting
        />
      );
    },
  },
  {
    accessorKey: 'createdAt',
    header: ({ column }) => {
      const { t } = useTranslation();
      return (
        <ColumnHeader
          column={column}
          title={t('datasets.table.createdAt')}
          enableSorting
        />
      );
    },
    cell: ({ row }) =>
      formatter.date.dateTime(new Date(row.original.createdAt as string)),
  },
  {
    accessorKey: 'createdBy',
    header: ({ column }) => {
      const { t } = useTranslation();
      return (
        <ColumnHeader
          column={column}
          title={t('datasets.table.createdBy')}
        />
      );
    },
    cell: ({ row }) => <span>{row.original.createdBy.email}</span>,
  },
  {
    id: 'actions',
    header: () => null,
    cell: ({ row }) => {
      const { t } = useTranslation();
      const router = useRouter();
      return (
        <DropdownMenu>
          <DropdownMenuTrigger>
            <Button variant={'outline'}>
              <Button.Icon icon>
                <EllipsisVertical />
              </Button.Icon>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                row.getToggleExpandedHandler()();
              }}>
              {row.getIsExpanded() ? <ChevronDown /> : <ChevronRight />}
              {t('datasets.table.expandable')}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={async () => {
                await router.navigate({
                  to: '/app/clusters',
                  search: {
                    datasetId: row.original.id,
                    isEval: true,
                  },
                });
              }}>
              <TestTube />
              {t('datasets.table.clusters')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];
